// @ts-strict-ignore
import { useRouter } from 'next/navigation'
import React from 'react'

import { useOpenAccessoriesOffer } from '@/features/cart/accessories-offer'
import { AddToCart } from '@/features/cart/add-to-cart'
import { NpiProductRequestButton } from '@/features/product/CreatePrereservation/ui/NpiProductRequestButton/NpiProductRequestButton'

import { TProductExtended } from '@/entities/card/types/types'

import { useGetCartItemsById } from '@/shared/lib/hooks/useGetCartItemsById'
import { CatalogItemLocation } from '@/shared/types/deprecated-types'
import { OrderType, ProductControl, TProduct } from '@/shared/types/product-types'
import { ButtonOwnProps } from '@/shared/ui'
import { Button } from '@/shared/ui'
import { Col, Row } from '@/shared/ui/Grid'
import { LoadingCircle } from '@/shared/ui/LoadingCircle'

import styles from './ProductButtons.module.scss'

export type ProductButtonsProps = {
	productInfo?: Partial<TProduct>
	big?: boolean
	isB2bDiscount?: boolean
	hideFastOrder?: boolean
	fastOrderBtnType?: ButtonOwnProps<any>['theme']
	location?: `${CatalogItemLocation}`
	isProductPage?: boolean
	buttonSize?: `S` | `M` | `L`
}

export const ProductButtons = ({
	productInfo,
	big,
	hideFastOrder = false,
	isProductPage = false,
	buttonSize = `L`
}: ProductButtonsProps): JSX.Element => {
	const router = useRouter()
	const { loadingAction, openAccessoriesOffer, openOrderWithAccessories } = useOpenAccessoriesOffer()

	const { ID, PRICE, BUTTON_NAME, KOD, ORDER_TYPE } = productInfo as TProductExtended
	const productPreview = { TITLE: productInfo.TITLE, IMAGE: productInfo.IMAGE || productInfo.IMAGES?.[0] }
	const productCartInfo = useGetCartItemsById(ID)
	const isPreOrder = ORDER_TYPE === OrderType.PreOrder || productInfo?.CONTROL?.includes(ProductControl.Preorder)
	const isMainActionLoading = loadingAction === 'cart' || loadingAction === 'preorder'
	const isFastOrderLoading = loadingAction === (ORDER_TYPE === OrderType.Reservation ? 'preorder' : 'fast')
	const openAfterCart = (addPromise: Promise<unknown>) =>
		openAccessoriesOffer({ action: 'cart', productCode: KOD, productId: ID, productPreview }, addPromise)

	const buttonName: string = BUTTON_NAME?.split('')
		.map((item, index) => (index === 0 ? item.toUpperCase() : item.toLowerCase()))
		.join('')

	const onRedirectToCartClick = async (): Promise<void> => {
		const isOpened = await openAccessoriesOffer({
			action: 'cart',
			productCode: KOD,
			productId: ID,
			productPreview
		})

		if (isOpened) return

		await router.push(`/cart`)
	}

	const onFastOrder = async event => {
		event.stopPropagation()

		await openOrderWithAccessories({
			action: ORDER_TYPE === OrderType.Reservation ? 'preorder' : 'fast',
			productCode: KOD,
			productId: ID,
			productPreview
		})
	}

	const onPreOrder = async event => {
		event.stopPropagation()
		event.preventDefault()
		await openOrderWithAccessories({ action: 'preorder', productCode: KOD, productId: ID, productPreview })
	}

	const productButtonsClasses = [styles.productButtons, big && styles.big].filter(Boolean).join(' ')

	const reservationButtonClasses = [styles.reservationButton, big && styles.big, isFastOrderLoading && styles.isLoading]
		.filter(Boolean)
		.join(' ')
	const isBuyHidden = productInfo?.CONTROL?.includes(ProductControl.HideBuy)

	if (isBuyHidden) return null

	return (
		<div className={productButtonsClasses}>
			{(() => {
				switch (ORDER_TYPE) {
					case OrderType.Reservation:
						return (
							<button className={`${reservationButtonClasses} gtm-fast-order`} onClick={onFastOrder}>
								{!isFastOrderLoading && (buttonName ?? 'Купить')}
								{isFastOrderLoading && <LoadingCircle />}
							</button>
						)
					case OrderType.HideBuy:
						return (
							<span
								style={{
									height: `4rem`,
									borderRadius: `3.2rem`,
									backgroundColor: `#efeff1`,
									display: `grid`,
									placeContent: `center`,
									placeItems: `center`,
									fontSize: ` 12px`,
									lineHeight: `18px`,
									margin: `0 auto`,
									color: `#333`,
									width: `100%`
								}}>
								{buttonName ?? 'Скоро в продаже'}
							</span>
						)
					case OrderType.Archive:
						return null
					case OrderType.Prereservation:
						return (
							<NpiProductRequestButton $modalContentData={productInfo} id="gtm-preservation-inform">
								{buttonName ?? (isProductPage ? `Сообщить о старте предзаказа` : `Сообщить о предзаказе`)}
							</NpiProductRequestButton>
						)

					default:
						return (
							<Row gutter={[14, 14]} align="middle">
								{PRICE.VALUE > 0 && (
									<Col xs={hideFastOrder ? 24 : 12} md={hideFastOrder ? 24 : 12}>
										{isPreOrder ? (
											<Button onClick={onPreOrder} isLoading={isMainActionLoading} disabled={isMainActionLoading}>
												{buttonName ?? 'Купить'}
											</Button>
										) : productCartInfo ? (
											<Button
												theme="outlined"
												onClick={onRedirectToCartClick}
												className={styles.redirectButton}
												isLoading={isMainActionLoading}
												disabled={isMainActionLoading}>
												{buttonName ?? 'Оформить'}
											</Button>
										) : (
											<AddToCart
												productData={productInfo}
												location={CatalogItemLocation.ProductCard}
												size={buttonSize}
												isPending={isMainActionLoading}
												onAddStarted={addPromise => void openAfterCart(addPromise)}
											/>
										)}
									</Col>
								)}
								{PRICE.VALUE > 0 && !hideFastOrder && (
									<Col xs={12} md={12}>
										<Button
											theme="outlined"
											className={`${styles.button} gtm-fast-order`}
											onClick={onFastOrder}
											isLoading={loadingAction === 'fast'}
											disabled={loadingAction === 'fast'}>
											Быстрый заказ
										</Button>
									</Col>
								)}
							</Row>
						)
				}
			})()}
		</div>
	)
}
